import { STOCK_FIELDS, type AnalyzeColumn, type Mapping, type StockField } from './types';

/**
 * Per-browser memory of "header text → field" from the operator's past confirmed imports. The same
 * supplier sends the same headers every month; once someone has told us that "Qté BL" is the
 * received quantity we should not ask again. A convenience only: storage may be unavailable, and
 * the server-side suggestion is always there underneath.
 */
const KEY = 'mecadepot.stockImport.columnMemory';

const norm = (h: string) =>
  h
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

function load(): Record<string, StockField> {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, StockField>) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function rememberMapping(columns: AnalyzeColumn[], mapping: Mapping): void {
  const memory = load();
  const mappedCols = new Map<number, StockField>();
  for (const f of STOCK_FIELDS) if (mapping[f] != null) mappedCols.set(mapping[f]!, f);
  for (const col of columns) {
    const h = norm(col.header);
    if (!h) continue;
    const field = mappedCols.get(col.index);
    // An explicitly ignored header is remembered as ignored too — forget any older assignment.
    if (field) memory[h] = field;
    else delete memory[h];
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(memory));
  } catch {
    /* storage unavailable — nothing to remember */
  }
}

/** Fields whose column is known from memory, by header text. */
export function recallMapping(columns: AnalyzeColumn[]): Partial<Record<StockField, number>> {
  const memory = load();
  const out: Partial<Record<StockField, number>> = {};
  for (const col of columns) {
    const field = memory[norm(col.header)];
    if (field && STOCK_FIELDS.includes(field) && out[field] == null && col.filled > 0)
      out[field] = col.index;
  }
  return out;
}
